// Account setup (the avatar opens Settings), sign-out, and the Team card shown on the Settings screen.

export interface Me {
  email: string;
  role: "admin" | "member";
  via: "access" | "session" | "dev";
  orgs: { id: string; name: string; role: "owner" | "viewer" }[];
  can_manage_team: boolean;
  can_manage_users?: boolean;
  can_edit?: string[];
}

interface TeamOrg {
  id: string;
  name: string;
  members: { user_id: string; email: string; role: "owner" | "viewer"; last_login_at: number | null }[];
}

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api/${path}`, {
    method,
    credentials: "same-origin",
    headers: body ? { "Content-Type": "application/json" } : {},
    body: body ? JSON.stringify(body) : undefined,
  });
  const out = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
  if (!res.ok) throw new Error((out as { error?: string }).error ?? `HTTP ${res.status}`);
  return out as T;
}

const ERRORS: Record<string, string> = {
  bad_email: "Enter a valid email address.",
  owners_add_viewers_only: "Owners can add viewers. Ask MonoBlock to add another owner.",
  owners_remove_viewers_only: "Only MonoBlock can remove an owner.",
};

export function initAccount(me: Me): void {
  const css = document.createElement("link");
  css.rel = "stylesheet";
  css.href = "/account.css";
  document.head.append(css);

  // The avatar opens Settings (account, users, team). It is a data-tab control, so app.ts's
  // click handler does the navigation and the keyboard handler covers Enter/Space.
  const avatar = document.getElementById("avatar");
  if (avatar) {
    avatar.dataset.tab = "settings";
    avatar.setAttribute("role", "button");
    avatar.setAttribute("tabindex", "0");
    avatar.setAttribute("aria-label", "Settings");
    avatar.removeAttribute("aria-hidden");
    avatar.title = "Settings";
  }
}

export async function signOut(me: Me): Promise<void> {
  if (me.via === "access") return void (location.href = "/cdn-cgi/access/logout");
  await fetch("/api/logout", { method: "POST", credentials: "same-origin" }).catch(() => null);
  location.href = "/login";
}

/** The Team card on the Settings screen: members of the orgs the caller owns. */
export async function renderTeam(box: HTMLElement, me: Me): Promise<void> {
  box.innerHTML = `<div class="card"><div class="card-head"><h2>Team</h2></div><div class="acct-msg">Loading…</div></div>`;
  try {
    const { orgs } = await call<{ orgs: TeamOrg[] }>("GET", "team");
    render(box, me, orgs);
  } catch (err) {
    box.querySelector(".acct-msg")!.textContent = `Couldn't load the team: ${(err as Error).message}`;
  }
}

function render(box: HTMLElement, me: Me, orgs: TeamOrg[]): void {
  const admin = me.role === "admin";
  const seen = (t: number | null) => (t ? `Signed in ${new Date(t).toLocaleDateString()}` : "Not signed in yet");
  const body = orgs.length
    ? orgs
        .map(
          (o) => `<section class="team-org" data-org="${esc(o.id)}">
  <h3>${esc(o.name)}</h3>
  ${
    o.members
      .map(
        (m) => `<div class="team-row"><span class="email">${esc(m.email)}</span><span class="role">${m.role}</span><span class="seen">${seen(m.last_login_at)}</span>${
          admin || m.role === "viewer" ? `<button class="link danger" data-remove="${esc(m.user_id)}" data-email="${esc(m.email)}">Remove</button>` : ""
        }</div>`,
      )
      .join("") || `<div class="acct-msg">No one has access yet.</div>`
  }
  <form class="team-add">
    <input class="acct-input" type="email" name="email" placeholder="name@company.com" aria-label="Email to invite" required>
    ${admin ? `<select class="btn" name="role" aria-label="Role"><option value="viewer">Viewer</option><option value="owner">Owner</option></select>` : ""}
    <button class="btn primary" type="submit">Invite${admin ? "" : " viewer"}</button>
  </form>
  <span class="acct-msg" role="status"></span>
</section>`,
        )
        .join("")
    : `<div class="acct-msg">No organisations to manage.</div>`;
  box.innerHTML = `<div class="card"><div class="card-head"><h2>Team</h2></div>
<p class="acct-msg" style="margin:0 0 12px">Viewers can see this organisation's dashboards. Owners can also edit goals and funnels and invite viewers.</p>${body}</div>`;

  for (const sec of box.querySelectorAll<HTMLElement>(".team-org")) {
    const org = sec.dataset.org!;
    const msg = sec.querySelector<HTMLElement>(":scope > .acct-msg[role=status]")!;
    const fail = (err: unknown) => {
      msg.textContent = ERRORS[(err as Error).message] ?? `Something went wrong (${(err as Error).message}).`;
      msg.className = "acct-msg error";
    };
    sec.querySelector("form")!.addEventListener("submit", async (e) => {
      e.preventDefault();
      const f = new FormData(e.target as HTMLFormElement);
      try {
        await call("POST", "team", { org, email: String(f.get("email") ?? ""), role: f.get("role") ?? "viewer" });
        await renderTeam(box, me);
      } catch (err) {
        fail(err);
      }
    });
    for (const b of sec.querySelectorAll<HTMLButtonElement>("[data-remove]")) {
      b.addEventListener("click", async () => {
        if (b.dataset.confirm !== "1") {
          b.dataset.confirm = "1";
          b.textContent = `Remove ${b.dataset.email}?`;
          return;
        }
        try {
          await call("DELETE", `team/${b.dataset.remove}?org=${encodeURIComponent(org)}`);
          await renderTeam(box, me);
        } catch (err) {
          fail(err);
        }
      });
    }
  }
}
